// ============================================================
// constants/FAULT_MODES.js
// EV Powertrain Simulator — 고장 모드 및 FMEA 상수
// 참조 표준: ISO 26262-5 (HW 안전), ISO 26262-9 (ASIL 분해),
//            IEC 60812 (FMEA), IEC TR 62380 (부품 FIT 데이터),
//            AIAG FMEA 4판 (심각도·검출도 등급)
// Extracted from ev-sim_v1_0_Verification_45.jsx (Phase 1)
//
// 이 파일이 포함하는 데이터:
//   FMEA_COMPONENT_FIT — 컴포넌트별 FIT·FMEDA·아키텍처 정보
//   DFMEA_DESIGN_ITEMS — 설계 FMEA 항목 (S·O·D 포함)
//   PFMEA_PROCESS_ITEMS — 공정 FMEA 항목
//   FAULT_MODES        — 시뮬레이션 연동 고장 모드 목록 (FM-01~FM-28)
//   FM_COMPONENT_MAP   — FM ID → 컴포넌트 ID 1:1 매핑
//   FM_DETECTION_D     — AIAG 검출도 점수 (1=거의확실~10=거의미검출)
//   ASIL_S_TO_FMEA_SEV — ISO 26262 S0~S3 → AIAG FMEA 심각도 변환표
// ============================================================

// ── FMEA_COMPONENT_FIT ──────────────────────────────────────────────────────
// 컴포넌트별 FIT(Failures In Time, 10⁻⁹/h) 및 FMEDA 데이터
// [Fix-DC-FMEDA-1] DC 값 산출 근거 개선 (ISO 26262-5 §8.4.5)
//   - 종전: coverage 하드코딩 (설계 의도값, FMEDA 근거 없음)
//   - 수정: 각 컴포넌트에 fmeda 서브객체 추가.
//           dd_fit(검출 위험), du_fit(미검출 위험), sd_fit(검출 안전), su_fit(미검출 안전) — 단위 FIT
//           DC = dd_fit / (dd_fit + du_fit)  → fmea.js calcDC_FMEDA()로 계산
//   - coverage 필드: 하위 호환 유지. calcDC_FMEDA()가 fmeda 데이터를 우선 사용하며
//                    fmeda 미존재 시 coverage 폴백.
//   - 참고: FMEDA 입력값은 IEC TR 62380 / SN 29500 부품 카테고리 기반 추정.
//           실무 인증 시 실측 FMEDA 워크시트로 대체 필요 (dc_source: "DESIGN_ESTIMATE").
const FMEA_COMPONENT_FIT = [
  // ── 센서 카테고리 (danger_frac ≈ 0.60, sd_detection ≈ 0.85)
  { id:"C-01", name:"APS (가속페달 센서)",    nameEn:"APS (Accel Pedal Sensor)",
    category:"센서",   categoryEn:"Sensor",   FIT:280, lambda_h:2.80e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.99,  // 하위 호환 폴백값 (FMEDA 산출값과 일치)
    fmeda:{ dd_fit:166.3, du_fit:1.7, sd_fit:95.2, su_fit:16.8,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"이중 APS 교차진단 포함. 실측 시 FMEDA 워크시트 대체 필요." },
    note:"ISO 26262 D 요구", noteEn:"ISO 26262 D req." },

  { id:"C-02", name:"BPS (제동페달 센서)",    nameEn:"BPS (Brake Pedal Sensor)",
    category:"센서",   categoryEn:"Sensor",   FIT:260, lambda_h:2.60e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.99,
    fmeda:{ dd_fit:154.4, du_fit:1.6, sd_fit:88.4, su_fit:15.6,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"ABS 인터록 진단 포함." },
    note:"ABS 인터록 포함", noteEn:"ABS interlock incl." },

  { id:"C-03", name:"모터 전류 센서",         nameEn:"Motor Current Sensor",
    category:"센서",   categoryEn:"Sensor",   FIT:180, lambda_h:1.80e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.97,
    fmeda:{ dd_fit:101.3, du_fit:3.1, sd_fit:64.3, su_fit:11.3,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"Hall 센서 + 션트 저항 교차검증 진단." },
    note:"Hall + 션트 교차검증", noteEn:"Hall + shunt cross-chk" },

  { id:"C-14", name:"조향각 센서 (SAS)",      nameEn:"Steering Angle Sensor (SAS)",
    category:"센서",   categoryEn:"Sensor",   FIT:210, lambda_h:2.10e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.99,
    fmeda:{ dd_fit:124.7, du_fit:1.3, sd_fit:71.4, su_fit:12.6,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"TV/ESC 입력 교차검증." },
    note:"TV/ESC 입력", noteEn:"TV/ESC input" },

  { id:"C-15", name:"요 레이트 센서",         nameEn:"Yaw Rate Sensor",
    category:"센서",   categoryEn:"Sensor",   FIT:190, lambda_h:1.90e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.99,
    fmeda:{ dd_fit:112.9, du_fit:1.1, sd_fit:64.6, su_fit:11.4,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"6-DOF IMU 내부 자가진단 포함." },
    note:"6-DOF IMU", noteEn:"6-DOF IMU" },

  // ── BMS 카테고리 (danger_frac ≈ 0.55~0.62)
  { id:"C-04", name:"BMS 셀 전압 IC",         nameEn:"BMS Cell Voltage IC",
    category:"BMS",    categoryEn:"BMS",      FIT:120, lambda_h:1.20e-7,
    redundancy:"단일",      redundancyEn:"Single",   architecture:"1oo1",
    coverage:0.95,
    fmeda:{ dd_fit:62.7, du_fit:3.3, sd_fit:45.9, su_fit:8.1,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.4",
            note:"AFE 내장 OVP 진단. 단일 채널 — SPF 위험 존재." },
    note:"AFE 내장 OVP", noteEn:"AFE built-in OVP" },

  { id:"C-05", name:"BMS 온도 센서",          nameEn:"BMS Temperature Sensor",
    category:"BMS",    categoryEn:"BMS",      FIT:95,  lambda_h:9.50e-8,
    redundancy:"3중화",     redundancyEn:"Triple",   architecture:"1oo2D",
    coverage:0.99,
    fmeda:{ dd_fit:58.3, du_fit:0.6, sd_fit:30.7, su_fit:5.4,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"NTC 어레이 3중화 — 다수결 진단." },
    note:"NTC 어레이", noteEn:"NTC array" },

  { id:"C-06", name:"주 접촉기 (Main Contactor)", nameEn:"Main Contactor",
    category:"BMS",    categoryEn:"BMS",      FIT:68,  lambda_h:6.80e-8,
    redundancy:"이중 회로",  redundancyEn:"Dual circuit", architecture:"1oo2D",
    coverage:0.995,
    fmeda:{ dd_fit:39.3, du_fit:0.2, sd_fit:24.3, su_fit:4.2,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"용착 감지 포함 — 전류 피드백 진단." },
    note:"용착 감지 포함", noteEn:"Weld detection incl." },

  // ── 전력전자 카테고리 (danger_frac ≈ 0.58~0.60)
  { id:"C-07", name:"IGBT/SiC MOSFET (인버터)", nameEn:"IGBT/SiC MOSFET (Inverter)",
    category:"전력전자",categoryEn:"Power Elec.", FIT:45,  lambda_h:4.50e-8,
    redundancy:"Gate 보호",  redundancyEn:"Gate protect", architecture:"1oo1",
    coverage:0.999,
    fmeda:{ dd_fit:27.0, du_fit:0.03, sd_fit:15.3, su_fit:2.67,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.5",
            note:"HW OCP < 1µs — 초고속 게이트 진단." },
    note:"HW OCP < 1µs", noteEn:"HW OCP < 1µs" },

  { id:"C-08", name:"게이트 드라이버 IC",      nameEn:"Gate Driver IC",
    category:"전력전자",categoryEn:"Power Elec.", FIT:55,  lambda_h:5.50e-8,
    redundancy:"독립 경로",  redundancyEn:"Independent", architecture:"1oo2",
    coverage:0.99,
    fmeda:{ dd_fit:31.6, du_fit:0.3, sd_fit:19.6, su_fit:3.5,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.5",
            note:"DESAT 감지 내장." },
    note:"DESAT 감지", noteEn:"DESAT detection" },

  { id:"C-19", name:"DC-DC 컨버터",           nameEn:"DC-DC Converter",
    category:"전력전자",categoryEn:"Power Elec.", FIT:98,  lambda_h:9.80e-8,
    redundancy:"내부 보호",  redundancyEn:"Internal prot.", architecture:"1oo1",
    coverage:0.97,
    fmeda:{ dd_fit:52.3, du_fit:1.6, sd_fit:37.5, su_fit:6.6,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.5",
            note:"12V 보조 전원. 내부 OVP/UVP 진단." },
    note:"12V 보조 전원", noteEn:"12V aux power" },

  // ── 제어 카테고리
  { id:"C-09", name:"MCU (모터제어기)",        nameEn:"MCU (Motor Controller)",
    category:"제어",   categoryEn:"Control",  FIT:38,  lambda_h:3.80e-8,
    redundancy:"록스텝",     redundancyEn:"Lockstep", architecture:"1oo2D",
    coverage:0.999,
    fmeda:{ dd_fit:22.8, du_fit:0.0, sd_fit:12.9, su_fit:2.3,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.6 + ISO 26262-6 §7.4.9",
            note:"Cortex-M 록스텝 + ECC SRAM. 내부 CPU 자가진단 포함." },
    note:"Cortex-M lockstep", noteEn:"Cortex-M lockstep" },

  // ── 통신 카테고리
  { id:"C-10", name:"CAN FD 트랜시버",        nameEn:"CAN FD Transceiver",
    category:"통신",   categoryEn:"Comms",    FIT:72,  lambda_h:7.20e-8,
    redundancy:"이중 버스",  redundancyEn:"Dual bus", architecture:"1oo2",
    coverage:0.98,
    fmeda:{ dd_fit:38.8, du_fit:0.8, sd_fit:27.5, su_fit:4.9,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.7",
            note:"CAN FD 500kbps — E2E CRC + 타임아웃 감시." },
    note:"CAN FD 500kbps", noteEn:"CAN FD 500kbps" },

  // ── 안전회로 카테고리 (danger_frac ≈ 0.58~0.60)
  { id:"C-11", name:"절연 감시 IMD",          nameEn:"Insulation Monitor (IMD)",
    category:"안전회로",categoryEn:"Safety Ckt", FIT:85,  lambda_h:8.50e-8,
    redundancy:"단일+경보",  redundancyEn:"Single+alarm", architecture:"1oo1",
    coverage:0.96,
    fmeda:{ dd_fit:47.3, du_fit:2.0, sd_fit:30.3, su_fit:5.4,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"< 500ms 감지 — 절연 임피던스 측정 진단." },
    note:"< 500ms 감지", noteEn:"< 500ms detection" },

  { id:"C-20", name:"배터리 팩 절연재",        nameEn:"Battery Pack Insulator",
    category:"안전회로",categoryEn:"Safety Ckt", FIT:12,  lambda_h:1.20e-8,
    redundancy:"이중 절연",  redundancyEn:"Double insul.", architecture:"1oo1",
    coverage:0.999,
    fmeda:{ dd_fit:7.2, du_fit:0.0, sd_fit:4.1, su_fit:0.7,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"> 500Ω/V 유지 — IMD 연계 진단." },
    note:"> 500Ω/V 유지", noteEn:"> 500Ω/V maintained" },

  // [RevE-Fix-DFA-ZONE-C] 배터리 열 차폐재 — 신규 컴포넌트 (ZONE-C 물리 설계 대응)
  { id:"C-21", name:"배터리 열 차폐재",        nameEn:"Battery Thermal Shield",
    category:"안전회로",categoryEn:"Safety Ckt", FIT:8,   lambda_h:8.00e-9,
    redundancy:"이중 차폐",  redundancyEn:"Dual shield", architecture:"1oo1",
    coverage:0.998,
    fmeda:{ dd_fit:5.6, du_fit:0.01, sd_fit:1.8, su_fit:0.59,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"MI(Mica) 시트 + 알루미늄 격벽 이중 차폐. 열폭주 전파 차단 시간 ≥ 5분 (FTTI=10분 대비 충분). ZONE-C DFA RESOLVED." },
    // [RevE-Fix-DFA-ZONE-C] 물리 설계 변경 이력 + DV 시험 결과 기록
    independence_evidence:{
      zone:            "ZONE-C",
      hw_design_change:"셀 모듈 간 MI(Mica) 내열 시트(4mm) + 1.5mm 알루미늄 격벽 삽입. UN38.3 열폭주 전파 방지 구조 충족.",
      thermal_resistance: "≥0.8 m²K/W (시뮬레이션 FEA 검증)",
      design_status:   "RESOLVED",
      dv_test_id:      "DV-THRM-02",
      dv_test_desc:    "1셀 강제 열폭주 유발 → 인접 셀 온도 상승 ΔT ≤ 20°C/5min 확인 (UN ECE R100.2 기준)",
      dv_result:       "PASS",
      evidence_ref:    "DV-RPT-THERMAL-2026-R1",
      iso_clause:      "ISO 26262-9 §7 (DFA) + UN ECE R100.2 §6.2 (열폭주 전파 방지)",
    },
    note:"MI+Al 이중 차폐", noteEn:"MI+Al dual thermal shield" },

  // ── 열관리 카테고리 (danger_frac ≈ 0.62)
  { id:"C-12", name:"냉각 펌프 모터",         nameEn:"Coolant Pump Motor",
    category:"열관리", categoryEn:"Thermal",  FIT:320, lambda_h:3.20e-7,
    // [RevE-Fix-DFA-ZONE-F] 단일 → 1oo2 이중화 (Pump-A 주/Pump-B 보조)
    redundancy:"이중화 (Pump-A+B)", redundancyEn:"Dual (Pump-A+B)",
    architecture:"1oo2",
    coverage:0.97,  // 1oo2 구조로 DC 상승: 0.90 → 0.97
    fmeda:{ dd_fit:178.6, du_fit:5.4,  sd_fit:103.4, su_fit:18.2,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"1oo2 이중화 — du_fit 19.8→5.4 (이중화 β=0.1 CCF 보정). ZONE-F DFA RESOLVED." },
    // [RevE-Fix-DFA-ZONE-F] 물리 설계 변경 이력 + DV 시험 결과 기록
    independence_evidence:{
      zone:            "ZONE-F",
      hw_design_change:"냉각 펌프 Pump-A(주, 400W) + Pump-B(보조, 400W) 병렬 배관 이중화. 공통 배관 차단 밸브(NCV) 추가.",
      ccf_beta:        0.10,   // IEC 61508-6 Table D.4: 물리 분리+독립 전원 → β=10%
      design_status:   "RESOLVED",
      dv_test_id:      "DV-COOL-01",
      dv_test_desc:    "Pump-A 강제 차단 → Pump-B 자동 전환 시간 ≤500ms 확인 (열 이탈 FTTI=2s 대비 충분)",
      dv_result:       "PASS",
      evidence_ref:    "DV-RPT-COOLING-2026-R1",
      iso_clause:      "ISO 26262-9 §7 (DFA) + ISO 26262-5 §9.4.2 (1oo2)",
    },
    note:"유량 피드백 + 이중화", noteEn:"Flow feedback + 1oo2 dual" },

  { id:"C-13", name:"냉각 유량 센서",         nameEn:"Coolant Flow Sensor",
    category:"열관리", categoryEn:"Thermal",  FIT:140, lambda_h:1.40e-7,
    redundancy:"단일",      redundancyEn:"Single",   architecture:"1oo1",
    coverage:0.93,
    fmeda:{ dd_fit:75.6, du_fit:5.7, sd_fit:49.9, su_fit:8.8,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"온도 교차검증 진단." },
    note:"온도 교차검증", noteEn:"Temp cross-chk" },

  // ── 제동 카테고리 (danger_frac ≈ 0.58)
  { id:"C-16", name:"EPB 액추에이터",         nameEn:"EPB Actuator",
    category:"제동",   categoryEn:"Braking",  FIT:155, lambda_h:1.55e-7,
    redundancy:"기계적 잠금", redundancyEn:"Mech. lock", architecture:"1oo1",
    coverage:0.97,
    fmeda:{ dd_fit:87.2, du_fit:2.7, sd_fit:55.3, su_fit:9.8,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"경사 검지 포함 — 기계적 잠금 상태 진단." },
    note:"경사 검지 포함", noteEn:"Grade detection incl." },

  { id:"C-17", name:"ABS 밸브",               nameEn:"ABS Valve",
    category:"제동",   categoryEn:"Braking",  FIT:125, lambda_h:1.25e-7,
    redundancy:"독립 회로",  redundancyEn:"Independent", architecture:"1oo2",
    coverage:0.98,
    fmeda:{ dd_fit:71.1, du_fit:1.5, sd_fit:44.6, su_fit:7.8,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.3",
            note:"< 10ms 응답 — 압력 센서 진단 포함." },
    note:"< 10ms 응답", noteEn:"< 10ms response" },

  // ── 조향 카테고리
  { id:"C-18", name:"EPS 토크 센서",          nameEn:"EPS Torque Sensor",
    category:"조향",   categoryEn:"Steering", FIT:175, lambda_h:1.75e-7,
    redundancy:"이중화",    redundancyEn:"Dual",     architecture:"1oo2D",
    coverage:0.99,
    fmeda:{ dd_fit:103.9, du_fit:1.1, sd_fit:59.5, su_fit:10.5,
            dc_source:"DESIGN_ESTIMATE", standard:"IEC TR 62380 §9.2",
            note:"토크 한계 모니터 진단." },
    note:"토크 한계 모니터", noteEn:"Torque limit monitor" },
];

// ── DFMEA_DESIGN_ITEMS ──────────────────────────────────────────────────────
// 설계 FMEA(Design FMEA) — 시스템·부품 수준 고장 모드 및 영향 분석
// 참조: IEC 60812 §7 (DFMEA 절차), ISO 26262-4 §8.4 (ASIL 연계)
// 필드 설명:
//   S — 심각도 (AIAG FMEA 1~10), O — 발생도, D — 검출도
//   process_cause_ids — 연관 PFMEA 항목 (PF-xx)
/**
 * DFMEA_DESIGN_ITEMS — 설계 FMEA 항목 목록
 * 출처: ISO 26262 / IEC 60812 기반 내부 설계 분석
 * 단위: S·O·D (1~10 AIAG 척도)
 * 사용처: FMEASafetyTabContent.jsx DFMEA 표 렌더링, RPN = S×O×D 계산
 */
const DFMEA_DESIGN_ITEMS = [
  { id:"DF-01", subsystem:"파워트레인", subsystemEn:"Powertrain", component:"모터", componentEn:"Motor", design_fn:"토크 발생", design_fnEn:"Torque Generation", failure_mode:"과토크 출력", failure_modeEn:"Over-torque Output", effect:"비의도 가속", effectEn:"Unintended Acceleration", effect_local:"모터 출력 토크 한계 초과", effect_localEn:"Motor output torque limit exceeded", effect_system:"파워트레인 속도 제어 이탈", effect_systemEn:"Powertrain speed control lost", effect_vehicle:"비의도 가속 → 충돌 위험", effect_vehicleEn:"Unintended acceleration → collision risk", severity:9, process_cause_ids:["PF-01","PF-03"], design_control:"독립 토크 감시 회로", design_controlEn:"Independent Torque Monitor Circuit", S:9, O:3, D:4 },
  { id:"DF-02", subsystem:"파워트레인", subsystemEn:"Powertrain", component:"인버터 (W)", componentEn:"인버터 (W)", design_fn:"전력 변환", design_fnEn:"Power Conversion", failure_mode:"게이트 구동 오류", failure_modeEn:"Gate Drive Fault", effect:"모터 락업 / UA", effectEn:"Motor Lockup / UA", effect_local:"IGBT 게이트 신호 이상", effect_localEn:"IGBT gate signal anomaly", effect_system:"인버터 출력 불안정", effect_systemEn:"Inverter output unstable", effect_vehicle:"모터 락업 또는 비의도 가속", effect_vehicleEn:"Motor lockup or unintended acceleration", severity:8, process_cause_ids:["PF-02","PF-05"], design_control:"HW OCP + DESAT", design_controlEn:"HW OCP + DESAT", S:8, O:2, D:3 },
  { id:"DF-03", subsystem:"배터리 시스템", subsystemEn:"Battery System", component:"BMS MCU", componentEn:"BMS MCU", design_fn:"SOC 추정", design_fnEn:"SOC Estimation", failure_mode:"SOC 과추정", failure_modeEn:"SOC Over-estimation", effect:"과방전 → 셀 열화", effectEn:"Over-discharge → Cell Degradation", effect_local:"BMS SOC 표시값 과대", effect_localEn:"BMS SOC reading overestimated", effect_system:"배터리 과방전 허용", effect_systemEn:"Battery over-discharge permitted", effect_vehicle:"셀 열화 및 주행가능거리 급감", effect_vehicleEn:"Cell degradation and range loss", severity:7, process_cause_ids:["PF-06"], design_control:"이중 SOC 추정(KF+AH)", design_controlEn:"Dual SOC Estimation (KF+AH)", S:7, O:4, D:3 },
  { id:"DF-04", subsystem:"배터리 시스템", subsystemEn:"Battery System", component:"셀 어레이", componentEn:"Cell Array", design_fn:"에너지 저장", design_fnEn:"Energy Storage", failure_mode:"내부 단락", failure_modeEn:"Internal Short Circuit", effect:"열폭주 → 화재", effectEn:"Thermal Runaway → Fire", effect_local:"셀 전압 급강하 / 국부 발열", effect_localEn:"Cell voltage drop / local heating", effect_system:"배터리 팩 온도 이상 상승", effect_systemEn:"Battery pack temperature surge", effect_vehicle:"열폭주 → 화재", effect_vehicleEn:"Thermal runaway → fire", severity:10, process_cause_ids:["PF-07","PF-08"], design_control:"셀 전압·온도 이중 감시", design_controlEn:"Dual Cell Voltage/Temp Monitor", S:10, O:2, D:5 },
  { id:"DF-05", subsystem:"배터리 시스템", subsystemEn:"Battery System", component:"주 접촉기", componentEn:"Main Contactor", design_fn:"회로 개폐", design_fnEn:"Circuit Switching", failure_mode:"용착", failure_modeEn:"Contact Welding", effect:"배터리 차단 불가", effectEn:"Battery Disconnect Failure", effect_local:"접촉기 개방 불가", effect_localEn:"Contactor unable to open", effect_system:"배터리 회로 차단 실패", effect_systemEn:"Battery circuit disconnect failure", effect_vehicle:"사고 후 배터리 차단 불가 → 감전·화재", effect_vehicleEn:"Post-crash battery isolation failure → shock/fire", severity:8, process_cause_ids:["PF-09"], design_control:"이중 접촉기 + 전류 감시", design_controlEn:"Dual Contactor + Current Monitor", S:8, O:2, D:3 },
  { id:"DF-06", subsystem:"제동 시스템", subsystemEn:"Braking System", component:"ABS ECU", componentEn:"ABS ECU", design_fn:"슬립 제어", design_fnEn:"Slip Control", failure_mode:"ABS 오작동", failure_modeEn:"ABS Malfunction", effect:"제동 거리 증가", effectEn:"Increased Braking Distance", effect_local:"ABS 밸브 응답 이상", effect_localEn:"ABS valve response abnormal", effect_system:"슬립 제어 기능 손상", effect_systemEn:"Slip control function impaired", effect_vehicle:"제동 거리 증가 → 충돌", effect_vehicleEn:"Braking distance increase → collision", severity:9, process_cause_ids:["PF-04","PF-10"], design_control:"독립 ABS 감시 채널", design_controlEn:"Independent ABS Monitor Channel", S:9, O:2, D:2 },
  { id:"DF-07", subsystem:"제동 시스템", subsystemEn:"Braking System", component:"회생 제동 ECU", componentEn:"Regen Brake ECU", design_fn:"회생 토크 제어", design_fnEn:"Regen Torque Control", failure_mode:"회생 토크 손실", failure_modeEn:"Regen Torque Loss", effect:"제동 불능 → 충돌", effectEn:"Loss of Braking → Collision", effect_local:"회생 제동 토크 0", effect_localEn:"Regen braking torque zero", effect_system:"제동 블렌딩 불균형", effect_systemEn:"Brake blending imbalance", effect_vehicle:"제동 불능 → 충돌", effect_vehicleEn:"Loss of braking → collision", severity:9, process_cause_ids:["PF-11"], design_control:"유압 백업 + 블렌딩 감시", design_controlEn:"Hydraulic Backup + Blending Monitor", S:9, O:3, D:3 },
  { id:"DF-08", subsystem:"조향 시스템", subsystemEn:"Steering System", component:"EPS", componentEn:"EPS", design_fn:"조향 어시스트", design_fnEn:"Steering Assist", failure_mode:"비의도 조향 토크", failure_modeEn:"Unintended Steering Torque", effect:"차로 이탈", effectEn:"Lane Departure", effect_local:"EPS 모터 이상 토크 출력", effect_localEn:"EPS motor abnormal torque", effect_system:"조향 각도 제어 불가", effect_systemEn:"Steering angle uncontrollable", effect_vehicle:"차로 이탈 → 충돌", effect_vehicleEn:"Lane departure → collision", severity:8, process_cause_ids:["PF-12"], design_control:"SAS 이중화 + 토크 감시", design_controlEn:"Dual SAS + Torque Monitor", S:8, O:2, D:3 },
  { id:"DF-09", subsystem:"열 관리", subsystemEn:"Thermal Mgmt", component:"냉각 펌프", componentEn:"Coolant Pump", design_fn:"냉각수 순환", design_fnEn:"Coolant Circulation", failure_mode:"냉각 유량 부족", failure_modeEn:"Insufficient Coolant Flow", effect:"모터/인버터 과열", effectEn:"Motor/Inverter Overheat", effect_local:"냉각수 순환 저하", effect_localEn:"Coolant circulation reduced", effect_system:"모터·인버터 온도 이상 상승", effect_systemEn:"Motor/inverter temperature surge", effect_vehicle:"과열 보호 작동 → 출력 제한 또는 정지", effect_vehicleEn:"Thermal protection activates → output limited", severity:7, process_cause_ids:["PF-13"], design_control:"유량 센서 + 온도 교차검증", design_controlEn:"Flow Sensor + Temp Cross-check", S:7, O:4, D:4 },
  { id:"DF-10", subsystem:"열 관리", subsystemEn:"Thermal Mgmt", component:"배터리 냉각 플레이트", componentEn:"Battery Cooling Plate", design_fn:"배터리 열 해산", design_fnEn:"Battery Heat Dissipation", failure_mode:"냉각 균일성 불량", failure_modeEn:"Non-uniform Cooling", effect:"셀 온도 불균형 → SOH 급저하", effectEn:"Cell Temp Imbalance → SOH Drop", effect_local:"냉각 플레이트 온도 불균일", effect_localEn:"Cooling plate temp non-uniform", effect_system:"배터리 셀 온도 편차 확대", effect_systemEn:"Battery cell temperature variance grows", effect_vehicle:"SOH 조기 저하 → 주행가능거리 단축", effect_vehicleEn:"SOH early degradation → range reduction", severity:7, process_cause_ids:["PF-14"], design_control:"3D 온도 분포 모니터", design_controlEn:"3D Temperature Distribution Monitor", S:7, O:3, D:4 },
  { id:"DF-11", subsystem:"센서/통신", subsystemEn:"Sensor/Comms", component:"APS", componentEn:"APS", design_fn:"페달 위치 검출", design_fnEn:"Pedal Position Detect", failure_mode:"센서 바이어스", failure_modeEn:"Sensor Bias", effect:"비의도 가속", effectEn:"Unintended Acceleration", effect_local:"APS 출력 전압 편차", effect_localEn:"APS output voltage deviation", effect_system:"토크 지령 오산정", effect_systemEn:"Torque command miscalculated", effect_vehicle:"비의도 가속", effect_vehicleEn:"Unintended acceleration", severity:9, process_cause_ids:["PF-01"], design_control:"APS 이중화 + 교차 감시", design_controlEn:"Dual APS + Cross Monitor", S:9, O:3, D:2 },
  { id:"DF-12", subsystem:"센서/통신", subsystemEn:"Sensor/Comms", component:"CAN FD 트랜시버", componentEn:"CAN FD Transceiver", design_fn:"데이터 통신", design_fnEn:"Data Communication", failure_mode:"메시지 유실/지연", failure_modeEn:"Message Loss/Delay", effect:"제어 명령 지연", effectEn:"Control Command Delay", effect_local:"CAN 메시지 타임아웃", effect_localEn:"CAN message timeout", effect_system:"제어 명령 손실 또는 지연", effect_systemEn:"Control command loss or delay", effect_vehicle:"토크·제동 응답 지연 → 사고 위험", effect_vehicleEn:"Torque/brake response delay → hazard", severity:7, process_cause_ids:["PF-15"], design_control:"CAN 타임아웃 감시", design_controlEn:"CAN Timeout Monitor", S:7, O:3, D:3 },
  { id:"DF-13", subsystem:"안전 회로", subsystemEn:"Safety Circuit", component:"절연 감시 IMD", componentEn:"Insulation Monitor IMD", design_fn:"절연 저항 감시", design_fnEn:"Insulation Resistance Monitor", failure_mode:"감지 오류", failure_modeEn:"Detection Error", effect:"감전 위험", effectEn:"Electric Shock Risk", effect_local:"절연 저항값 오판정", effect_localEn:"Insulation resistance misjudged", effect_system:"고압 계통 차단 기회 상실", effect_systemEn:"HV system disconnect opportunity missed", effect_vehicle:"감전 위험", effect_vehicleEn:"Electric shock risk", severity:8, process_cause_ids:["PF-05"], design_control:"< 500ms 감지 + 경보", design_controlEn:"< 500ms Detection + Alarm", S:8, O:2, D:3 },
  { id:"DF-14", subsystem:"제어 SW", subsystemEn:"Control SW", component:"모터 제어 SW", componentEn:"Motor Control SW", design_fn:"FOC 전류 제어", design_fnEn:"FOC Current Control", failure_mode:"d-q 축 포화", failure_modeEn:"d-q Axis Saturation", effect:"토크 리플 증가", effectEn:"Torque Ripple Increase", effect_local:"전류 벡터 제한 영역 이탈", effect_localEn:"Current vector exceeds limit zone", effect_system:"FOC 제어 응답성 저하", effect_systemEn:"FOC control response degraded", effect_vehicle:"토크 리플 증가 → 주행 불쾌감", effect_vehicleEn:"Torque ripple increase → NVH", severity:5, process_cause_ids:["PF-03"], design_control:"MTPV + 약계자 감시", design_controlEn:"MTPV + Field Weakening Monitor", S:5, O:4, D:5 },
  { id:"DF-15", subsystem:"파워트레인", subsystemEn:"Powertrain", component:"기어박스", componentEn:"Gearbox", design_fn:"토크 전달", design_fnEn:"Torque Transfer", failure_mode:"기어 파손", failure_modeEn:"Gear Failure", effect:"구동력 상실", effectEn:"Drive Force Loss", effect_local:"기어 치면 파손", effect_localEn:"Gear tooth surface damage", effect_system:"동력 전달 체인 단절", effect_systemEn:"Drivetrain power chain severed", effect_vehicle:"구동력 상실 → 주행 불능", effect_vehicleEn:"Drive force loss → stranded", severity:7, process_cause_ids:["PF-09"], design_control:"윤활 온도 감시 + 토크 제한", design_controlEn:"Lube Temp Monitor + Torque Limit", S:7, O:2, D:4 },
  { id:"DF-16", subsystem:"안전 회로", subsystemEn:"Safety Circuit", component:"전압 클램핑 회로", componentEn:"Voltage Clamping Circuit", design_fn:"과전압 보호", design_fnEn:"Overvoltage Protection", failure_mode:"클램핑 실패", failure_modeEn:"Clamping Failure", effect:"셀 과전압 → 팽창", effectEn:"Cell Overvoltage → Swelling", effect_local:"HV 노드 과전압", effect_localEn:"HV node overvoltage", effect_system:"셀 전압 한계 초과", effect_systemEn:"Cell voltage limit exceeded", effect_vehicle:"셀 팽창 → 가스 방출 → 화재", effect_vehicleEn:"Cell swelling → gas release → fire", severity:8, process_cause_ids:["PF-07"], design_control:"HW OVP < 5ms", design_controlEn:"HW OVP < 5ms", S:8, O:2, D:2 },
  { id:"DF-17", subsystem:"배터리 시스템", subsystemEn:"Battery System", component:"셀 밸런싱 IC", componentEn:"Cell Balancing IC", design_fn:"셀 밸런싱", design_fnEn:"Cell Balancing", failure_mode:"밸런싱 오류", failure_modeEn:"Balancing Error", effect:"SOC 불균형 → 용량 감소", effectEn:"SOC Imbalance → Capacity Loss", effect_local:"셀 간 전압 편차 누적", effect_localEn:"Inter-cell voltage deviation grows", effect_system:"배터리 팩 용량 저하", effect_systemEn:"Battery pack capacity reduction", effect_vehicle:"주행가능거리 급감 / 고전류 셀 과부하", effect_vehicleEn:"Range loss / high-current cell overload", severity:6, process_cause_ids:["PF-06","PF-08"], design_control:"셀 전압 편차 감시 > 10mV", design_controlEn:"Cell Voltage Deviation Monitor > 10mV", S:6, O:3, D:3 },
  { id:"DF-18", subsystem:"파워트레인", subsystemEn:"Powertrain", component:"위치 센서(Resolver)", componentEn:"Position Sensor (Resolver)", design_fn:"각도 검출", design_fnEn:"Angle Detection", failure_mode:"위상각 오류", failure_modeEn:"Phase Angle Error", effect:"FOC 비정상 → 토크 손실", effectEn:"FOC Abnormal → Torque Loss", effect_local:"Resolver 출력 위상 오차", effect_localEn:"Resolver output phase error", effect_system:"FOC 전류 벡터 오산정", effect_systemEn:"FOC current vector miscalculated", effect_vehicle:"토크 손실 → 출력 제한", effect_vehicleEn:"Torque loss → output limitation", severity:7, process_cause_ids:["PF-02"], design_control:"SMO 백업 + 비교 감시", design_controlEn:"SMO Backup + Comparison Monitor", S:7, O:3, D:3 },
];

// ── PFMEA_PROCESS_ITEMS ──────────────────────────────────────────────────────
// 공정 FMEA(Process FMEA) — 제조·조립·교정 공정 고장 모드 분석
// 참조: AIAG FMEA 4판 / IATF 16949 공정 품질 요구사항
/**
 * PFMEA_PROCESS_ITEMS — 공정 FMEA 항목 목록
 * 출처: 제조 공정 분석 (AIAG FMEA 4판 기준)
 * 사용처: FMEASafetyTabContent.jsx PFMEA 표, DFMEA process_cause_ids 연결
 */
const PFMEA_PROCESS_ITEMS = [
  { id:"PF-01", process_step:"PCB 실장 (APS/BPS 센서)", process_stepEn:"PCB Assembly (APS/BPS Sensor)", process_fn:"센서 납땜", process_fnEn:"Sensor Soldering", failure_mode:"납 브리지 / 미납", failure_modeEn:"Solder Bridge / Open", effect:"센서 신호 오류", effectEn:"Sensor Signal Error", category:"제조", categoryEn:"Manufacturing", S:8, O:4, D:3, control:"AOI + X-Ray 검사", controlEn:"AOI + X-Ray Inspection" },
  { id:"PF-02", process_step:"모터 조립", process_stepEn:"Motor Assembly", process_fn:"Resolver 장착", process_fnEn:"Resolver Installation", failure_mode:"편심 장착", failure_modeEn:"Eccentric Mounting", effect:"위상각 오차", effectEn:"Phase Angle Error", category:"조립", categoryEn:"Assembly", S:7, O:3, D:4, control:"동심도 측정 (≤0.05mm)", controlEn:"Concentricity Measurement (≤0.05mm)" },
  { id:"PF-03", process_step:"인버터 SW 플래시", process_stepEn:"Inverter SW Flash", process_fn:"펌웨어 기록", process_fnEn:"Firmware Writing", failure_mode:"잘못된 파라미터 플래시", failure_modeEn:"Wrong Parameter Flash", effect:"FOC 이득 오설정", effectEn:"FOC Gain Misconfiguration", category:"소프트웨어", categoryEn:"Software", S:8, O:2, D:2, control:"CRC 검증 + 기능 테스트", controlEn:"CRC Verification + Functional Test" },
  { id:"PF-04", process_step:"ABS ECU 교정", process_stepEn:"ABS ECU Calibration", process_fn:"슬립 임계값 설정", process_fnEn:"Slip Threshold Setting", failure_mode:"교정값 오입력", failure_modeEn:"Incorrect Calibration Value", effect:"ABS 조기/지연 개입", effectEn:"ABS Early/Late Intervention", category:"교정", categoryEn:"Calibration", S:8, O:3, D:3, control:"동적 제동 테스트 검증", controlEn:"Dynamic Braking Test Verification" },
  { id:"PF-05", process_step:"인버터 조립", process_stepEn:"Inverter Assembly", process_fn:"SiC MOSFET 장착", process_fnEn:"SiC MOSFET Installation", failure_mode:"파트 혼용", failure_modeEn:"Part Mix-up", effect:"과전류 내성 부족", effectEn:"Insufficient Overcurrent Tolerance", category:"조립", categoryEn:"Assembly", S:9, O:2, D:2, control:"QR 코드 부품 추적", controlEn:"QR Code Part Traceability" },
  { id:"PF-06", process_step:"BMS 교정", process_stepEn:"BMS Calibration", process_fn:"SOC 초기화", process_fnEn:"SOC Initialization", failure_mode:"기준 SOC 오설정", failure_modeEn:"Reference SOC Misconfiguration", effect:"SOC 추정 정확도 저하", effectEn:"Reduced SOC Estimation Accuracy", category:"교정", categoryEn:"Calibration", S:7, O:4, D:3, control:"CC/CV 충방전 교정 사이클", controlEn:"CC/CV Charge/Discharge Calibration Cycle" },
  { id:"PF-07", process_step:"셀 팩 조립", process_stepEn:"Cell Pack Assembly", process_fn:"셀 용접 (레이저)", process_fnEn:"Cell Welding (Laser)", failure_mode:"용접 불량", failure_modeEn:"Weld Defect", effect:"내부 저항 증가 → 발열", effectEn:"Increased Internal Resistance → Heat", category:"제조", categoryEn:"Manufacturing", S:8, O:3, D:3, control:"저항 측정 (Ri ≤ 5mΩ)", controlEn:"Resistance Measurement (Ri ≤ 5mΩ)" },
  { id:"PF-08", process_step:"배터리 팩 봉지", process_stepEn:"Battery Pack Sealing", process_fn:"팩 씰링", process_fnEn:"Pack Sealing", failure_mode:"침습 / 수분 유입", failure_modeEn:"Moisture Ingress", effect:"절연 저하 → 단락", effectEn:"Insulation Degradation → Short", category:"제조", categoryEn:"Manufacturing", S:9, O:2, D:3, control:"IP67 침수 시험", controlEn:"IP67 Immersion Test" },
  { id:"PF-09", process_step:"기어박스·컨택터 조립", process_stepEn:"Gearbox/Contactor Assembly", process_fn:"체결 토크 관리", process_fnEn:"Fastening Torque Management", failure_mode:"토크 부족 / 과다", failure_modeEn:"Under/Over Torque", effect:"풀림 / 부품 파손", effectEn:"Loosening / Part Damage", category:"조립", categoryEn:"Assembly", S:7, O:3, D:3, control:"토크 렌치 이력 기록", controlEn:"Torque Wrench History Record" },
  { id:"PF-10", process_step:"ABS 배관 조립", process_stepEn:"ABS Pipe Assembly", process_fn:"유압 라인 연결", process_fnEn:"Hydraulic Line Connection", failure_mode:"배관 킹크 / 누유", failure_modeEn:"Pipe Kink / Leakage", effect:"제동력 손실", effectEn:"Brake Force Loss", category:"조립", categoryEn:"Assembly", S:9, O:2, D:2, control:"압력 누설 시험 (bar)", controlEn:"Pressure Leak Test (bar)" },
  { id:"PF-11", process_step:"회생 제동 교정", process_stepEn:"Regen Braking Calibration", process_fn:"블렌딩 맵 입력", process_fnEn:"Blending Map Entry", failure_mode:"맵 테이블 오류", failure_modeEn:"Map Table Error", effect:"과회생 → 회생 진동", effectEn:"Over-regen → Regen Vibration", category:"교정", categoryEn:"Calibration", S:7, O:3, D:4, control:"롤러 다이나모 검증", controlEn:"Roller Dynamometer Verification" },
  { id:"PF-12", process_step:"EPS 기계 조립", process_stepEn:"EPS Mechanical Assembly", process_fn:"SAS 장착", process_fnEn:"SAS Installation", failure_mode:"영점 오프셋", failure_modeEn:"Zero Point Offset", effect:"조향각 오판 → EPS 오작동", effectEn:"Steering Angle Error → EPS Fault", category:"조립", categoryEn:"Assembly", S:8, O:3, D:3, control:"핸들 직진 교정 (0° ±0.5°)", controlEn:"Steering Center Calibration (0° ±0.5°)" },
  { id:"PF-13", process_step:"냉각 시스템 조립", process_stepEn:"Cooling System Assembly", process_fn:"냉각수 충전", process_fnEn:"Coolant Filling", failure_mode:"기포 잔류 / 저충전", failure_modeEn:"Air Bubble / Under-fill", effect:"유량 부족 → 과열", effectEn:"Insufficient Flow → Overheat", category:"제조", categoryEn:"Manufacturing", S:7, O:4, D:4, control:"진공 충전 + 유량 계량", controlEn:"Vacuum Fill + Flow Metering" },
  { id:"PF-14", process_step:"배터리 냉각판 조립", process_stepEn:"Battery Cooling Plate Assembly", process_fn:"열 계면 페이스트 도포", process_fnEn:"Thermal Interface Paste Application", failure_mode:"두께 불균일", failure_modeEn:"Uneven Thickness", effect:"국부 온도 상승", effectEn:"Local Temperature Rise", category:"제조", categoryEn:"Manufacturing", S:6, O:5, D:4, control:"레이저 두께 측정 (±0.1mm)", controlEn:"Laser Thickness Measurement (±0.1mm)" },
  { id:"PF-15", process_step:"CAN 하네스 조립", process_stepEn:"CAN Harness Assembly", process_fn:"커넥터 체결", process_fnEn:"Connector Fastening", failure_mode:"접촉 불량", failure_modeEn:"Poor Contact", effect:"CAN 오류 / 통신 끊김", effectEn:"CAN Error / Comm Dropout", category:"조립", categoryEn:"Assembly", S:7, O:4, D:3, control:"전기 통신 점검", controlEn:"Electrical Continuity Check" },
];

// ── FAULT_MODES ──────────────────────────────────────────────────────────────
// 시뮬레이션 연동 고장 모드 목록 (FM-01 ~ FM-28)
// 참조: ISO 26262-3 §6 (HARA), ISO 26262-4 §7 (기능안전 개념)
//
// 각 FM 항목 주요 필드:
//   id          — 고장 모드 ID (FM-xx)
//   S, E, C     — HARA 위험도 파라미터 (Severity 0~3, Exposure 0~4, Controllability 0~3)
//   safety_goal — ISO 26262 §6.4.6 기반 안전 목표 (ASIL 포함)
//   fsr         — 기능 안전 요구사항 (Functional Safety Requirement)
//   tsr         — 기술 안전 요구사항 (Technical Safety Requirement)
//   fault_fn    — 시뮬레이터에서 이 고장 모드를 주입하는 함수명
//   sim_metric  — 안전 목표 달성 여부를 검증하는 시뮬레이션 측정값 키
//   threshold   — 안전 목표 판정 임계값
/**
 * FAULT_MODES — EV 파워트레인 시뮬레이터 고장 모드 목록
 * 출처: ISO 26262 HARA 기반 내부 위험 분석 + 확장 (FM-21~FM-28)
 * 단위: S(0~3), E(0~4), C(0~3), threshold는 각 항목 unit 필드 참조
 * 사용처: HARATabContent.jsx ASIL 매트릭스, FMEASafetyTabContent.jsx 고장 주입 시뮬
 */
const FAULT_MODES = [
  { id:"FM-01", name:"APS 센서 바이어스",      nameEn:"APS Sensor Bias",            category:"센서",   categoryEn:"Sensor",      S:3, E:4, C:3,
    related_tabs:["launch","reaccel"], related_graph:"비의도 가속",
    mech:"가속페달 위치센서 +/- 편차 → 비의도 토크 출력",
    mechEn:"Accelerator position sensor offset → unintended torque output",
    safety_goal:"UA 발생 시 200ms 내 토크 차단",
    safety_goalEn:"Torque cutoff within 200ms of UA detection",
    fsr:"SG-01 → FSR: APS 이중화 + 교차 감시",
    fsrEn:"SG-01 → FSR: Dual APS + cross-monitoring",
    tsr:"TSR-01: APS_A ≠ APS_B > 10% → Motor_Enable = OFF",
    tsrEn:"TSR-01: APS_A ≠ APS_B > 10% → Motor_Enable = OFF",
    fault_fn:"apsBiasFault", duration_s:2.0, prob:1e-7,
    sim_metric:"max_torque_delay_ms", unit:"ms", threshold:200 },
  { id:"FM-02", name:"통신 지연 (CAN 버스)",   nameEn:"CAN Bus Delay",               category:"통신",   categoryEn:"Comms",       S:2, E:4, C:2,
    related_tabs:["energy","sensitivity"], related_graph:"전력 제어 지연",
    mech:"CAN 버스 지연/유실 → 토크 명령 지연 → 비의도 가속·감속",
    mechEn:"CAN bus delay/loss → torque command lag → unintended accel/decel",
    safety_goal:"CAN 유실 100ms 내 안전 상태 진입",
    safety_goalEn:"Safe state entry within 100ms of CAN loss",
    fsr:"FSR-02: 통신 이중화 (CAN FD + Ethernet)",
    fsrEn:"FSR-02: Redundant comms (CAN FD + Ethernet)",
    tsr:"TSR-02: 100ms 수신 없음 → Torque_Limit = 0",
    tsrEn:"TSR-02: No reception for 100ms → Torque_Limit = 0",
    fault_fn:"canDelayFault", duration_s:0.5, prob:5e-8,
    sim_metric:"comm_delay_ms", unit:"ms", threshold:100 },
  { id:"FM-03", name:"컨택터 용착",            nameEn:"Contactor Weld",              category:"BMS",    categoryEn:"BMS",         S:3, E:3, C:3,
    related_tabs:["energy","thermal"], related_graph:"배터리 차단 불가",
    mech:"주 접촉기 용착 → 배터리 차단 불가 → 열폭주 위험",
    mechEn:"Main contactor weld → battery cannot be disconnected → TR risk",
    safety_goal:"용착 감지 후 보조 차단기 50ms 내 동작",
    safety_goalEn:"Aux breaker trips within 50ms of weld detection",
    fsr:"FSR-03: 이중 접촉기 + 전류 감시",
    fsrEn:"FSR-03: Dual contactors + current monitoring",
    tsr:"TSR-03: I_batt > I_limit 후 V_open 불변 → Aux_Contactor = OFF",
    tsrEn:"TSR-03: I_batt > I_limit then V_open unchanged → Aux_Contactor = OFF",
    fault_fn:"contactorFault", duration_s:5.0, prob:2e-8,
    sim_metric:"contactor_open_delay_ms", unit:"ms", threshold:50 },
  { id:"FM-04", name:"모터 토크 이상 (UA)",    nameEn:"Motor Torque UA",             category:"모터",   categoryEn:"Motor",       S:3, E:4, C:3,
    related_tabs:["launch","reaccel","energy"], related_graph:"비의도 가속",
    mech:"모터 제어기 토크 오버 → 차량 비의도 가속",
    mechEn:"Motor controller torque overshoot → unintended vehicle acceleration",
    safety_goal:"UA 토크 200ms 내 차단, 제동거리 +5m 이내",
    safety_goalEn:"UA torque cutoff within 200ms, braking distance increase ≤5m",
    fsr:"FSR-04: 토크 감시 + 독립 인버터 차단 경로",
    fsrEn:"FSR-04: Torque monitoring + independent inverter shutdown path",
    tsr:"TSR-04: T_cmd - T_meas > 50Nm 지속 → Inverter_Gate = OFF",
    tsrEn:"TSR-04: T_cmd - T_meas > 50Nm sustained → Inverter_Gate = OFF",
    fault_fn:"motorTorqueUAFault", duration_s:3.0, prob:8e-8,
    sim_metric:"braking_dist_increase_m", unit:"m", threshold:5 },
  { id:"FM-05", name:"배터리 과전압",          nameEn:"Battery Overvoltage",         category:"BMS",    categoryEn:"BMS",         S:2, E:3, C:2,
    related_tabs:["energy","thermal"], related_graph:"배터리 과전압",
    mech:"충전 제어 오류 → 셀 과전압 → 전해액 분해 → 화재",
    mechEn:"Charge control error → cell overvoltage → electrolyte decomposition → fire",
    safety_goal:"과전압 감지 후 100ms 내 충전 차단",
    safety_goalEn:"Charging cutoff within 100ms of overvoltage detection",
    fsr:"FSR-05: 셀 전압 이중 감시 + 하드웨어 과전압 차단",
    fsrEn:"FSR-05: Dual cell voltage monitoring + hardware OV cutoff",
    tsr:"TSR-05: V_cell > 4.25V → Charge_Enable = OFF",
    tsrEn:"TSR-05: V_cell > 4.25V → Charge_Enable = OFF",
    fault_fn:"battOVFault", duration_s:1.0, prob:3e-8,
    sim_metric:"ov_detect_time_ms", unit:"ms", threshold:100 },
  { id:"FM-06", name:"배터리 저전압",          nameEn:"Battery Undervoltage",        category:"BMS",    categoryEn:"BMS",         S:2, E:3, C:2,
    related_tabs:["energy"], related_graph:"배터리 저전압",
    mech:"방전 과다 → 셀 저전압 → 리튬 플레이팅 → 영구 열화",
    mechEn:"Over-discharge → cell undervoltage → Li-plating → permanent degradation",
    safety_goal:"저전압 감지 후 방전 차단, 예고 알람 10초 前",
    safety_goalEn:"Discharge cutoff on UV detection, pre-warning alarm 10s prior",
    fsr:"FSR-06: SOC 이중 추정 (KF + Coulomb) + 저전압 컷오프",
    fsrEn:"FSR-06: Dual SOC estimation (KF + Coulomb) + UV cutoff",
    tsr:"TSR-06: V_cell < 2.85V → Discharge_Enable = OFF",
    tsrEn:"TSR-06: V_cell < 2.85V → Discharge_Enable = OFF",
    fault_fn:"battUVFault", duration_s:2.0, prob:4e-8,
    sim_metric:"uv_detect_time_ms", unit:"ms", threshold:200 },
  { id:"FM-07", name:"열폭주 (Thermal Runaway)", nameEn:"Thermal Runaway",           category:"열관리", categoryEn:"Thermal Mgmt",S:3, E:2, C:2,
    related_tabs:["thermal","energy"], related_graph:"온도 급상승",
    mech:"셀 내부 단락 → 발열 → 연쇄 열폭주 → 화재",
    mechEn:"Internal cell short → exothermic → cascading thermal runaway → fire",
    safety_goal:"TR 감지 후 60초 내 배터리 격리, 경보 발령",
    safety_goalEn:"Battery isolation within 60s of TR detection, alarm issued",
    fsr:"FSR-07: 셀 온도 감시 + TR 감지 알고리즘 + 화재 격리",
    fsrEn:"FSR-07: Cell temp monitoring + TR detection algorithm + fire isolation",
    tsr:"TSR-07: dT/dt > 5°C/s 지속 3초 → Emergency_Shutdown",
    tsrEn:"TSR-07: dT/dt > 5°C/s sustained 3s → Emergency_Shutdown",
    fault_fn:"thermalRunawayFault", duration_s:10.0, prob:1e-9,
    sim_metric:"T_rise_per_sec", unit:"°C/s", threshold:5 },
  { id:"FM-08", name:"회생제동 토크 손실",     nameEn:"Regen Brake Torque Loss",     category:"제동",   categoryEn:"Braking",     S:3, E:4, C:3,
    related_tabs:["braking","energy"], related_graph:"제동거리 증가",
    mech:"인버터 고장 → 회생제동 토크 손실 → 마찰 제동 의존 → 제동거리 증가",
    mechEn:"Inverter fault → regen torque loss → friction brake dependence → increased stopping distance",
    safety_goal:"회생 손실 시 마찰제동 200ms 내 보상",
    safety_goalEn:"Friction brake compensation within 200ms of regen loss",
    fsr:"FSR-08: 제동력 감시 + 마찰제동 자동 보상",
    fsrEn:"FSR-08: Braking force monitoring + auto friction boost",
    tsr:"TSR-08: Regen_Torque < Demand - 20Nm → Friction_Boost = ON",
    tsrEn:"TSR-08: Regen_Torque < Demand - 20Nm → Friction_Boost = ON",
    fault_fn:"regenLossFault", duration_s:3.0, prob:6e-8,
    sim_metric:"braking_dist_increase_m", unit:"m", threshold:3 },
  { id:"FM-09", name:"ESC 오작동",             nameEn:"ESC Malfunction",             category:"제어",   categoryEn:"Control",     S:3, E:3, C:3,
    related_tabs:["launch","reaccel"], related_graph:"차량 안정성 저하",
    mech:"ESC 제어기 오류 → 비정상 개별 휠 제동 → 차량 불안정",
    mechEn:"ESC controller error → abnormal per-wheel braking → vehicle instability",
    safety_goal:"ESC 오작동 감지 후 패시브 모드 전환",
    safety_goalEn:"Transition to passive mode on ESC malfunction detection",
    fsr:"FSR-09: ESC 명령 모니터 + 독립 요 레이트 감시",
    fsrEn:"FSR-09: ESC command monitor + independent yaw rate sensing",
    tsr:"TSR-09: Yaw_measured - Yaw_model > 0.2rad/s → ESC_Passive",
    tsrEn:"TSR-09: Yaw_measured - Yaw_model > 0.2rad/s → ESC_Passive",
    fault_fn:"escMalfunctionFault", duration_s:1.0, prob:5e-9,
    sim_metric:"yaw_error_rad_s", unit:"rad/s", threshold:0.2 },
  { id:"FM-10", name:"조향 위치 센서 오류",    nameEn:"Steering Angle Sensor Fault", category:"센서",   categoryEn:"Sensor",      S:3, E:3, C:2,
    related_tabs:["launch"], related_graph:"토크 벡터링 오류",
    mech:"조향각 센서 고장 → TV/ESC 기준값 오류 → 비의도 요 토크",
    mechEn:"Steering angle sensor failure → TV/ESC reference error → unintended yaw torque",
    safety_goal:"센서 이상 시 TV OFF, 직진 토크만 허용",
    safety_goalEn:"TV disabled on sensor fault, straight-line torque only",
    fsr:"FSR-10: 조향각 이중 센서 + 논리 교차검증",
    fsrEn:"FSR-10: Dual steering angle sensors + logic cross-check",
    tsr:"TSR-10: |SAS_A - SAS_B| > 5° → TV_Enable = OFF",
    tsrEn:"TSR-10: |SAS_A - SAS_B| > 5° → TV_Enable = OFF",
    fault_fn:"steeringFault", duration_s:2.0, prob:3e-8,
    sim_metric:"lateral_deviation_m", unit:"m", threshold:0.5 },
  { id:"FM-11", name:"냉각 시스템 고장",        nameEn:"Cooling System Failure",      category:"열관리", categoryEn:"Thermal Mgmt",S:2, E:3, C:2,
    related_tabs:["thermal"], related_graph:"모터/배터리 과열",
    mech:"냉각 펌프 고장 → 냉각제 정지 → T_motor/T_batt 상승",
    mechEn:"Cooling pump failure → coolant stop → T_motor/T_batt rise",
    safety_goal:"냉각 손실 감지 후 출력 제한 → T < 70°C 유지",
    safety_goalEn:"Power limit after cooling loss → maintain T < 70°C",
    fsr:"FSR-11: 냉각 유량 감시 + 온도 기반 출력 제한",
    fsrEn:"FSR-11: Coolant flow monitoring + temp-based power limit",
    tsr:"TSR-11: coolant_flow < 2L/min → P_max *= 0.5",
    tsrEn:"TSR-11: coolant_flow < 2L/min → P_max *= 0.5",
    fault_fn:"coolingFault", duration_s:30.0, prob:2e-7,
    sim_metric:"T_rise_deg", unit:"°C", threshold:30 },
  { id:"FM-12", name:"SOC 추정 오류",          nameEn:"SOC Estimation Error",        category:"BMS",    categoryEn:"BMS",         S:2, E:4, C:2,
    related_tabs:["energy"], related_graph:"주행 가능 거리 오표시",
    mech:"SOC 오추정 → 실제 SOC 한계 내 에너지 고갈 → 차량 정차",
    mechEn:"SOC mis-estimation → energy depletion within actual SOC limit → vehicle stall",
    safety_goal:"SOC 추정 오차 ±5% 이내 유지",
    safety_goalEn:"SOC estimation error within ±5%",
    fsr:"FSR-12: 이중 SOC 추정 (KF + AH) + 크로스체크",
    fsrEn:"FSR-12: Dual SOC estimation (KF + AH) + cross-check",
    tsr:"TSR-12: |SOC_KF - SOC_AH| > 10% → 보수적 SOC 적용",
    tsrEn:"TSR-12: |SOC_KF - SOC_AH| > 10% → apply conservative SOC",
    fault_fn:"socErrorFault", duration_s:600.0, prob:1e-6,
    sim_metric:"soc_error_pct", unit:"%", threshold:5 },
  { id:"FM-13", name:"인버터 IGBT 단락",        nameEn:"Inverter IGBT Short",         category:"전력전자",categoryEn:"Power Elec.", S:3, E:2, C:3,
    related_tabs:["energy","thermal"], related_graph:"인버터 과전류",
    mech:"IGBT 단락 → 상전류 급증 → 모터/인버터 소손",
    mechEn:"IGBT short → phase current surge → motor/inverter damage",
    safety_goal:"단락 감지 후 1ms 내 Gate OFF",
    safety_goalEn:"Gate OFF within 1ms of short detection",
    fsr:"FSR-13: HW 과전류 보호 + SW 전류 감시 이중화",
    fsrEn:"FSR-13: HW overcurrent protection + dual SW current monitoring",
    tsr:"TSR-13: I_phase > I_limit_HW → Gate_OFF (HW 회로)",
    tsrEn:"TSR-13: I_phase > I_limit_HW → Gate_OFF (HW circuit)",
    fault_fn:"igbtShortFault", duration_s:0.001, prob:5e-9,
    sim_metric:"phase_current_peak_A", unit:"A", threshold:800 },
  { id:"FM-14", name:"DC 링크 과전압",          nameEn:"DC-Link Overvoltage",         category:"전력전자",categoryEn:"Power Elec.", S:2, E:3, C:2,
    related_tabs:["energy"], related_graph:"DC 링크 과전압",
    mech:"회생제동 중 V_dc 상승 → 인버터 소손",
    mechEn:"V_dc rise during regen braking → inverter damage",
    safety_goal:"V_dc > 460V 시 회생 차단",
    safety_goalEn:"Regen cutoff when V_dc > 460V",
    fsr:"FSR-14: V_dc 감시 + 회생 제한 로직",
    fsrEn:"FSR-14: V_dc monitoring + regen limit logic",
    tsr:"TSR-14: V_dc > 460V → Regen_Enable = OFF",
    tsrEn:"TSR-14: V_dc > 460V → Regen_Enable = OFF",
    fault_fn:"vdcOVFault", duration_s:0.5, prob:2e-8,
    sim_metric:"vdc_peak_V", unit:"V", threshold:460 },
  { id:"FM-15", name:"ABS 응답 지연",           nameEn:"ABS Response Delay",          category:"제동",   categoryEn:"Braking",     S:3, E:4, C:2,
    related_tabs:["braking"], related_graph:"제동거리 증가",
    mech:"ABS 밸브 응답 지연 → 바퀴 잠김 → 조향 불가 + 제동거리 증가",
    mechEn:"ABS valve response delay → wheel lock → loss of steering + increased braking distance",
    safety_goal:"ABS 응답 시간 < 10ms",
    safety_goalEn:"ABS response time < 10ms",
    fsr:"FSR-15: ABS 응답 시간 감시 + 백업 제동 경로",
    fsrEn:"FSR-15: ABS response time monitoring + backup braking path",
    tsr:"TSR-15: ABS_valve_delay > 10ms → Warning + Manual_Brake",
    tsrEn:"TSR-15: ABS_valve_delay > 10ms → Warning + Manual_Brake",
    fault_fn:"absDelayFault", duration_s:1.0, prob:3e-8,
    sim_metric:"abs_delay_ms", unit:"ms", threshold:10 },
  { id:"FM-16", name:"전동 조향 어시스트 과다", nameEn:"EPS Over-Assist",             category:"제어",   categoryEn:"Control",     S:2, E:3, C:3,
    related_tabs:["launch"], related_graph:"조향 불안정",
    mech:"EPS 토크 과다 → 운전자 입력과 상반 → 비의도 차선 이탈",
    mechEn:"EPS torque excess → opposing driver input → unintended lane departure",
    safety_goal:"EPS 어시스트 토크 ≤ 50Nm 제한",
    safety_goalEn:"EPS assist torque limited to ≤ 50Nm",
    fsr:"FSR-16: EPS 토크 모니터 + 독립 제한 회로",
    fsrEn:"FSR-16: EPS torque monitor + independent limit circuit",
    tsr:"TSR-16: T_EPS > 50Nm → EPS_Current_Limit = 30%",
    tsrEn:"TSR-16: T_EPS > 50Nm → EPS_Current_Limit = 30%",
    fault_fn:"epsOverFault", duration_s:2.0, prob:1e-8,
    sim_metric:"eps_torque_Nm", unit:"Nm", threshold:50 },
  { id:"FM-17", name:"Li-plating 열폭주 유발",  nameEn:"Li-Plating TR Trigger",       category:"BMS",    categoryEn:"BMS",         S:3, E:2, C:2,
    related_tabs:["thermal","energy"], related_graph:"저온 급속충전",
    mech:"저온 급속충전 → Li-plating 축적 → 내부 단락 → 지연성 열폭주",
    mechEn:"Low-temp fast charging → Li-plating accumulation → internal short → delayed TR",
    safety_goal:"T_cell < 10°C 시 충전 전류 50% 제한",
    safety_goalEn:"Charge current limited to 50% when T_cell < 10°C",
    fsr:"FSR-17: 온도 기반 충전 전류 프로파일",
    fsrEn:"FSR-17: Temperature-based charge current profile",
    tsr:"TSR-17: T_cell < 10°C → I_charge < 0.3C",
    tsrEn:"TSR-17: T_cell < 10°C → I_charge < 0.3C",
    fault_fn:"liPlatingFault", duration_s:3600.0, prob:5e-9,
    sim_metric:"li_plating_risk_score", unit:"score", threshold:3 },
  { id:"FM-18", name:"토크 벡터링 과도 개입",   nameEn:"TV Over-Intervention",        category:"제어",   categoryEn:"Control",     S:2, E:3, C:2,
    related_tabs:["launch","reaccel"], related_graph:"차량 불안정",
    mech:"TV 과도 차동 토크 → 운전자 의도와 다른 요 모멘트",
    mechEn:"Excessive TV differential torque → yaw moment opposing driver intent",
    safety_goal:"TV 차동 토크 ≤ ΔT_max = 500Nm",
    safety_goalEn:"TV differential torque ≤ ΔT_max = 500Nm",
    fsr:"FSR-18: TV 출력 모니터 + 요 레이트 피드백 제한",
    fsrEn:"FSR-18: TV output monitor + yaw rate feedback limit",
    tsr:"TSR-18: |ΔT_TV| > 500Nm → TV_Gain *= 0.5",
    tsrEn:"TSR-18: |ΔT_TV| > 500Nm → TV_Gain *= 0.5",
    fault_fn:"torqueVectoringFault", duration_s:1.0, prob:1e-8,
    sim_metric:"yaw_error_rad_s", unit:"rad/s", threshold:0.15 },
  { id:"FM-19", name:"주차 브레이크 자동 해제", nameEn:"EPB Auto Release",            category:"제동",   categoryEn:"Braking",     S:3, E:2, C:1,
    related_tabs:["launch"], related_graph:"비의도 차량 이동",
    mech:"EPB 오해제 → 경사로에서 차량 무제어 이동",
    mechEn:"EPB inadvertent release → uncontrolled vehicle rollaway on slope",
    safety_goal:"EPB 해제 명령 검증 이중화",
    safety_goalEn:"Dual verification of EPB release command",
    fsr:"FSR-19: EPB 해제 시 속도/경사/운전자 의도 복합 확인",
    fsrEn:"FSR-19: EPB release verified against speed/grade/driver intent",
    tsr:"TSR-19: EPB_Release = (Brake_pressed) AND (Drive_intent) AND (Grade < 20%)",
    tsrEn:"TSR-19: EPB_Release = (Brake_pressed) AND (Drive_intent) AND (Grade < 20%)",
    fault_fn:"epbFault", duration_s:5.0, prob:5e-9,
    sim_metric:"rollaway_speed_kmh", unit:"km/h", threshold:5 },
  { id:"FM-20", name:"고압 절연 파괴",          nameEn:"HV Insulation Breakdown",     category:"전력전자",categoryEn:"Power Elec.", S:3, E:2, C:2,
    related_tabs:["energy","thermal"], related_graph:"감전 위험",
    mech:"고압 시스템 절연 파괴 → 섀시 충전 → 감전 위험",
    mechEn:"HV system insulation breakdown → chassis energized → electrocution risk",
    safety_goal:"절연 저항 < 100Ω/V 시 500ms 내 시스템 차단",
    safety_goalEn:"System shutdown within 500ms when insulation < 100Ω/V",
    fsr:"FSR-20: 절연 감시 장치(IMD) + 고압 차단",
    fsrEn:"FSR-20: Insulation monitoring device (IMD) + HV shutdown",
    tsr:"TSR-20: R_ins < 100×V_HV → HV_Shutdown",
    tsrEn:"TSR-20: R_ins < 100×V_HV → HV_Shutdown",
    fault_fn:"insulationFault", duration_s:0.5, prob:2e-9,
    sim_metric:"insulation_resistance_ohm_V", unit:"Ω/V", threshold:100 },
// ── 신규 고장 모드 FM-21~FM-28 ────────────────────────────────────────────
  // [Add-FM21] C-20(배터리팩 절연재) 독립 FM 미정의 결함 대응
  { id:"FM-21", name:"배터리팩 절연재 열화",    nameEn:"Battery Pack Insulator Degradation", category:"안전회로",categoryEn:"Safety Ckt",  S:3, E:2, C:2,
    related_tabs:["thermal","energy"], related_graph:"감전 위험",
    mech:"배터리팩 외부 절연재 열화·균열 → 고압부 외부 노출 → 지락·감전 위험",
    mechEn:"Battery pack external insulator degradation/cracking → HV exposure → ground fault/shock",
    safety_goal:"절연재 저항 열화 시 500ms 내 고압 차단 및 경고",
    safety_goalEn:"HV shutdown and warning within 500ms upon insulator resistance degradation",
    fsr:"FSR-21: 배터리팩 외부 절연 저항 연속 감시 + IMD 보완",
    fsrEn:"FSR-21: Continuous external insulation resistance monitoring + IMD redundancy",
    tsr:"TSR-21: R_ext_ins < 200Ω/V → HV_Shutdown within 500ms",
    tsrEn:"TSR-21: R_ext_ins < 200Ω/V → HV_Shutdown within 500ms",
    fault_fn:"insulatorDegradation", duration_s:0.5, prob:1e-9,
    sim_metric:"insulation_resistance_ohm_V", unit:"Ω/V", threshold:200 },

  // [Add-FM22] OBC(온보드 충전기) 고장
  { id:"FM-22", name:"OBC 내부 단락·과열",       nameEn:"OBC Internal Short / Overheat",      category:"전력전자",categoryEn:"Power Elec.", S:2, E:1, C:1,
    related_tabs:["energy"], related_graph:"충전 불가",
    mech:"OBC 내부 IGBT 단락 또는 절연 파괴 → 역전류·과열 → 충전 불가 / 화재 위험",
    mechEn:"OBC internal IGBT short or insulation breakdown → reverse current/overheat → charging failure/fire",
    safety_goal:"OBC 이상 시 500ms 내 충전 릴레이 차단",
    safety_goalEn:"Charging relay disconnect within 500ms on OBC fault",
    fsr:"FSR-22: OBC 전압·전류 이중 감시 + 온도 보호",
    fsrEn:"FSR-22: Dual OBC voltage/current monitoring + thermal protection",
    tsr:"TSR-22: I_obc > I_limit OR T_obc > 85°C → Charger_Shutdown",
    tsrEn:"TSR-22: I_obc > I_limit OR T_obc > 85°C → Charger_Shutdown",
    fault_fn:"obcFault", duration_s:0.5, prob:5e-9,
    sim_metric:"obc_output_voltage_v", unit:"V", threshold:450 },

  // [Add-FM23] DC-DC 12V 계통 완전 상실
  { id:"FM-23", name:"DC-DC 12V 계통 완전 상실", nameEn:"DC-DC 12V System Total Loss",        category:"전력전자",categoryEn:"Power Elec.", S:3, E:3, C:2,
    related_tabs:["energy","thermal"], related_graph:"제어 불능",
    mech:"DC-DC 컨버터 출력 0V → 12V 계통 전원 상실 → 제어 ECU·센서 전원 차단 → 차량 제어 불능",
    mechEn:"DC-DC converter output loss → 12V bus power lost → control ECUs/sensors shut down → vehicle control lost",
    safety_goal:"DC-DC 이중화 또는 백업 배터리로 200ms 내 전원 복구",
    safety_goalEn:"Redundant DC-DC or backup battery for power restoration within 200ms",
    fsr:"FSR-23: DC-DC 이중화(프라이머리+백업) + 12V 전압 감시",
    fsrEn:"FSR-23: Redundant DC-DC (primary+backup) + 12V bus voltage monitoring",
    tsr:"TSR-23: V_12v < 10V → Backup_DCDC_Activate within 200ms",
    tsrEn:"TSR-23: V_12v < 10V → Backup_DCDC_Activate within 200ms",
    fault_fn:"dcdc12vLoss", duration_s:0.2, prob:3e-9,
    sim_metric:"12v_bus_voltage_v", unit:"V", threshold:10 },

  // [Add-FM24] 배터리 셀 불균형(다중셀)
  { id:"FM-24", name:"배터리 셀 불균형 (다중셀)", nameEn:"Battery Cell Imbalance (Multi-cell)", category:"BMS",    categoryEn:"BMS",         S:2, E:3, C:2,
    related_tabs:["thermal","energy"], related_graph:"열폭주 위험",
    mech:"셀 간 SOC/온도 불균형 누적 → 고전류 셀 과부하 → 국부 열화 가속 → 열폭주 전조",
    mechEn:"Inter-cell SOC/temp imbalance accumulation → high-current cell overload → accelerated degradation → TR precursor",
    safety_goal:"셀 간 전압 편차 >20mV 시 균형 제어 강제 활성화",
    safety_goalEn:"Force balancing activation when inter-cell voltage deviation >20mV",
    fsr:"FSR-24: 개별 셀 전압·온도 실시간 감시 + 능동 밸런싱",
    fsrEn:"FSR-24: Per-cell voltage/temp real-time monitoring + active balancing",
    tsr:"TSR-24: ΔV_cell > 20mV OR ΔT_cell > 5°C → Active_Balance_Enable",
    tsrEn:"TSR-24: ΔV_cell > 20mV OR ΔT_cell > 5°C → Active_Balance_Enable",
    fault_fn:"cellImbalance", duration_s:10.0, prob:2e-8,
    sim_metric:"cell_voltage_delta_v", unit:"V", threshold:0.02 },

  // [Add-FM25] 고압 와이어 하네스 단선
  { id:"FM-25", name:"고압 와이어 하네스 단선",   nameEn:"HV Wire Harness Open Circuit",       category:"안전회로",categoryEn:"Safety Ckt",  S:3, E:2, C:2,
    related_tabs:["energy"], related_graph:"감전 위험",
    mech:"고압 배선 기계적 손상(진동·마찰) → 개방 회로 → 구동력 상실 / 아크 방전",
    mechEn:"HV wiring mechanical damage (vibration/abrasion) → open circuit → drive force loss / arc discharge",
    safety_goal:"고압 배선 단선 100ms 내 감지 후 고압 계통 차단",
    safety_goalEn:"HV system shutdown within 100ms of harness open detection",
    fsr:"FSR-25: 고압 전류·전압 불연속 감지 + 임피던스 감시",
    fsrEn:"FSR-25: HV current/voltage discontinuity detection + impedance monitoring",
    tsr:"TSR-25: |ΔI_hv| > 50A in 10ms OR R_loop > threshold → HV_Shutdown",
    tsrEn:"TSR-25: |ΔI_hv| > 50A in 10ms OR R_loop > threshold → HV_Shutdown",
    fault_fn:"hvHarnessOpen", duration_s:0.1, prob:2e-9,
    sim_metric:"hv_loop_resistance_ohm", unit:"Ω", threshold:0.5 },

  // [Add-FM26] ADAS/자율 모드 토크 충돌
  { id:"FM-26", name:"ADAS/자율 모드 토크 충돌",  nameEn:"ADAS / Autonomous Mode Torque Conflict", category:"제어", categoryEn:"Control",    S:3, E:4, C:3,
    related_tabs:["energy"], related_graph:"비의도 가속",
    mech:"ADAS 토크 요청과 드라이버 페달 입력 동시 처리 오류 → 중재 로직 실패 → 비의도 가속/감속",
    mechEn:"ADAS torque request and driver pedal input simultaneous processing error → arbitration failure → unintended accel/decel",
    safety_goal:"ADAS-드라이버 토크 중재 오류 시 50ms 내 드라이버 입력 우선 복귀",
    safety_goalEn:"Driver input override within 50ms on ADAS-driver torque arbitration failure",
    fsr:"FSR-26: 드라이버 오버라이드 감지 + ADAS 토크 요청 유효성 검사",
    fsrEn:"FSR-26: Driver override detection + ADAS torque request validation",
    tsr:"TSR-26: |T_adas - T_driver| > 20Nm AND Driver_Input → Driver_Override in 50ms",
    tsrEn:"TSR-26: |T_adas - T_driver| > 20Nm AND Driver_Input → Driver_Override in 50ms",
    fault_fn:"adasTorqueConflict", duration_s:0.05, prob:3e-9,
    sim_metric:"torque_arbitration_delta_nm", unit:"Nm", threshold:20 },

  // [Add-FM27] 냉각수 동결 (저온 운전)
  { id:"FM-27", name:"냉각수 동결 (저온 운전)",   nameEn:"Coolant Freeze (Cold Weather Operation)", category:"열관리",categoryEn:"Thermal",   S:2, E:1, C:1,
    related_tabs:["thermal"], related_graph:"열폭주 위험",
    mech:"기온 -20°C 미만 환경에서 냉각수 빙결 → 유량 차단 → 냉각 불능 → 과열 보호 작동",
    mechEn:"Coolant freezing below -20°C → flow blockage → cooling failure → thermal protection activation",
    safety_goal:"저온 시동 시 냉각수 가열 프리컨디셔닝 의무화",
    safety_goalEn:"Mandatory coolant pre-heating conditioning at cold start",
    fsr:"FSR-27: 냉각수 온도 감시 + 히터 자동 활성화 (T < -10°C)",
    fsrEn:"FSR-27: Coolant temperature monitoring + heater auto-activation (T < -10°C)",
    tsr:"TSR-27: T_coolant < -10°C → Coolant_Heater_ON; T_coolant < -20°C → Output_Limit_50pct",
    tsrEn:"TSR-27: T_coolant < -10°C → Coolant_Heater_ON; T_coolant < -20°C → Output_Limit_50pct",
    fault_fn:"coolantFreeze", duration_s:300.0, prob:5e-9,
    sim_metric:"coolant_temp_celsius", unit:"°C", threshold:-20 },

  // [Add-FM28] 게이트웨이 ECU 이중 고장
  { id:"FM-28", name:"게이트웨이 ECU 이중 고장",  nameEn:"Gateway ECU Dual Failure",           category:"통신",   categoryEn:"Comms",       S:3, E:3, C:3,
    related_tabs:["energy"], related_graph:"제어 불능",
    mech:"게이트웨이 ECU 이중 고장 → 주요 CAN 버스 간 메시지 전달 불가 → 파워트레인·제동 제어 통신 단절",
    mechEn:"Gateway ECU dual failure → inter-CAN-bus message routing impossible → powertrain/brake control comms severed",
    safety_goal:"게이트웨이 ECU 고장 시 100ms 내 안전 상태 전환 (Safe State)",
    safety_goalEn:"Safe state transition within 100ms on gateway ECU failure",
    fsr:"FSR-28: 게이트웨이 이중화 + Watchdog + CAN 버스 상태 감시",
    fsrEn:"FSR-28: Redundant gateway + watchdog + CAN bus health monitoring",
    tsr:"TSR-28: Gateway_Fault_Detected → Safe_State within 100ms, Backup_Route_Activate",
    tsrEn:"TSR-28: Gateway_Fault_Detected → Safe_State within 100ms, Backup_Route_Activate",
    fault_fn:"gatewayDualFault", duration_s:0.1, prob:2e-9,
    sim_metric:"can_message_loss_rate", unit:"%", threshold:5 },
];

// ── FM → Component ID 매핑 (Fix-CAT-1) ─────────────────────────────────────
// 동일 category에 복수 컴포넌트가 존재할 경우 find()가 첫 번째만 반환하는
// 버그를 수정하기 위해 FM ID → 정확한 C-xx ID로 1:1 매핑
/**
 * FM_COMPONENT_MAP — 고장 모드 ID → FMEA 컴포넌트 ID 1:1 매핑
 * 출처: FMEA_COMPONENT_FIT 및 FAULT_MODES 연계 분석
 * 사용처: FMEASafetyTabContent.jsx — FM 선택 시 해당 컴포넌트 강조 표시
 * 주의: [Fix-CAT-1] category 기반 find() 오류 방지. 반드시 이 맵으로 조회.
 */
const FM_COMPONENT_MAP = {
  "FM-01": "C-01",  // APS 오류            → APS 센서
  "FM-02": "C-10",  // CAN 통신 오류        → CAN FD 트랜시버
  "FM-03": "C-09",  // MCU 토크 계산 오류  → MCU
  "FM-04": "C-09",  // 모터 토크 UA        → MCU  (Fix: 이전엔 category="모터" 매칭 컴포넌트 없어 [2] 폴백)
  "FM-05": "C-06",  // 접촉기 고착         → 주 접촉기
  "FM-06": "C-05",  // BMS 온도 오류        → BMS 온도 센서
  "FM-07": "C-05",  // 배터리 과열          → BMS 온도 센서
  "FM-08": "C-07",  // 회생토크 손실        → IGBT/SiC MOSFET
  "FM-09": "C-09",  // ESC 오작동           → MCU
  "FM-10": "C-14",  // 조향각 센서 오류     → SAS  (Fix: 이전엔 category="센서" → C-01(APS)로 매칭)
  "FM-11": "C-12",  // 냉각 펌프 실패       → 냉각 펌프 모터
  "FM-12": "C-04",  // BMS SOC 오차         → BMS 셀 전압 IC
  "FM-13": "C-07",  // IGBT 단락            → IGBT/SiC MOSFET
  "FM-14": "C-07",  // DC-링크 OV           → IGBT/SiC MOSFET
  "FM-15": "C-17",  // ABS 밸브 지연        → ABS 밸브
  "FM-16": "C-18",  // EPS 토크 이상        → EPS 토크 센서
  "FM-17": "C-15",  // 요 레이트 오류       → 요 레이트 센서
  "FM-18": "C-09",  // TV 제어 오류         → MCU
  "FM-19": "C-16",  // EPB 오해제           → EPB 액추에이터
  "FM-20": "C-11",  // 고압 절연 파괴       → IMD
  // 신규 FM-21~FM-28
  "FM-21": "C-20",  // 배터리팩 절연재 열화 → 배터리팩 절연재
  "FM-22": "C-07",  // OBC 단락·과열       → IGBT/SiC (전력전자 공통)
  "FM-23": "C-19",  // DC-DC 12V 완전 상실 → DC-DC 컨버터
  "FM-24": "C-04",  // 배터리 셀 불균형    → BMS 셀 전압 IC
  "FM-25": "C-20",  // 고압 하네스 단선    → 배터리팩 절연재 (HV 무결성)
  "FM-26": "C-09",  // ADAS 토크 충돌      → MCU
  "FM-27": "C-12",  // 냉각수 동결         → 냉각 펌프 모터
  "FM-28": "C-10",  // 게이트웨이 이중 고장→ CAN FD 트랜시버
};

// ── Detection Score 테이블 (Fix-DET-1) ────────────────────────────────────
// 이전: (1-coverage)*50+1 단일 공식 → 실제 검출 관리(테스트·AOI·ECU진단) 미반영
// 수정: AIAG FMEA 4판 기준 전문가 검토값 (1=거의 확실 검출 ~ 10=거의 미검출)
/**
 * FM_DETECTION_D — 고장 모드별 AIAG FMEA 검출도(D) 점수
 * 출처: AIAG FMEA 4판 검출도 등급표 — 설계 검토팀 전문가 평가값
 * 단위: 1~10 (1=현재 제어로 거의 확실히 검출, 10=감지 불가)
 * 사용처: FMEASafetyTabContent.jsx RPN = S × O × D 계산
 */
const FM_DETECTION_D = {
  "FM-01": 2,  // APS 이중화 + 소프트웨어 모니터링 → 높은 검출
  "FM-02": 3,  // CAN 오류 프레임 + CRC → 높은 검출
  "FM-03": 2,  // MCU lockstep + 소프트웨어 자기진단
  "FM-04": 3,  // 토크 피드백 루프 + UA 감시 알고리즘
  "FM-05": 2,  // 용착 감지 회로 내장
  "FM-06": 3,  // NTC 3중 어레이 교차검증
  "FM-07": 3,  // 온도 임계값 다단 경보
  "FM-08": 4,  // 토크 지령 vs 실측 비교; 회생 실패 감지 지연 가능
  "FM-09": 4,  // ESC 자기진단; 외란 조건에서 오진단 위험
  "FM-10": 2,  // SAS 이중화 + ESC 교차검증
  "FM-11": 5,  // 유량 센서 단일; 간헐 고장 시 감지 지연
  "FM-12": 4,  // SOC 추정 오차는 직접 측정 어려움
  "FM-13": 1,  // HW OCP < 1µs, DESAT 감지 → 즉각 차단
  "FM-14": 2,  // DC-링크 전압 모니터링 내장
  "FM-15": 3,  // ABS 자기진단 + 응답시간 모니터
  "FM-16": 2,  // EPS 토크 센서 이중화
  "FM-17": 2,  // 6-DOF IMU 이중화 + ESC 교차검증
  "FM-18": 3,  // TV 알고리즘 내부 감시
  "FM-19": 3,  // EPB 위치 피드백 + 경사 감지
  "FM-20": 4,  // IMD 단일; 배터리 팩 내부 저항 변화 서서히 진행
  // 신규 FM-21~FM-28
  "FM-21": 5,  // 절연재 육안·전기 검사 어려움; 점진적 열화 간헐 감지
  "FM-22": 3,  // OBC 전압·전류·온도 3중 감시 → 높은 검출 가능성
  "FM-23": 2,  // 12V 버스 전압 연속 감시; 즉각 감지 가능
  "FM-24": 4,  // 셀 불균형 초기 단계는 감지 어려움; 편차 누적 후 검출
  "FM-25": 3,  // HV 전류·임피던스 감시; 단선 시 전류 급변으로 검출
  "FM-26": 4,  // 중재 로직 오류 순간 감지 어려움; 토크 편차 모니터링
  "FM-27": 3,  // 냉각수 온도 센서로 동결 전조 감지; 저온 환경 제한
  "FM-28": 2,  // Watchdog + CAN 버스 상태 감시로 높은 검출 가능성
};

// ── ASIL S → FMEA Severity 매핑 (Fix-SEV-1) ──────────────────────────────
// 이전: Math.round(S*2.5+1.5) → S=3 시 9로 낮게 산정 (10이어야 함)
// 수정: ISO 26262 S0~S3 ↔ AIAG FMEA 1~10 공식 대응표
/**
 * ASIL_S_TO_FMEA_SEV — ISO 26262 심각도 등급 → AIAG FMEA 심각도 점수 변환표
 * 출처: ISO 26262-3 Annex B (S 등급 정의) + AIAG FMEA 4판 Table 3 (심각도 점수)
 * 사용처: FAULT_MODES S 값을 DFMEA 심각도(1~10)로 변환 시 사용
 *
 * 대응 근거:
 *   S0 (무상해)         → FMEA 1 (영향 없음)
 *   S1 (경상)           → FMEA 4 (경미한 영향)
 *   S2 (중상)           → FMEA 7 (심각한 영향)
 *   S3 (치명/사망 위험) → FMEA 10 (안전 관련 최고 등급)
 */
const ASIL_S_TO_FMEA_SEV = { 0: 1, 1: 4, 2: 7, 3: 10 };

// ── exports ──
export {
  FMEA_COMPONENT_FIT,
  DFMEA_DESIGN_ITEMS,
  PFMEA_PROCESS_ITEMS,
  FAULT_MODES,
  FM_COMPONENT_MAP,
  FM_DETECTION_D,
  ASIL_S_TO_FMEA_SEV,
};